/**
 * Theme Provider
 * Provides theme context to the application with dark mode support
 */

import { ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { theme } from '@/theme';

/**
 * Global styles
 * Applies global CSS reset and custom styles
 */
const globalStyles = (
  <GlobalStyles
    styles={{
      // Smooth scrolling
      html: {
        scrollBehavior: 'smooth',
      },

      // Box sizing
      '*, *::before, *::after': {
        boxSizing: 'border-box',
      },

      // Body defaults
      body: {
        margin: 0,
        padding: 0,
        overflowX: 'hidden',
      },

      // Remove default focus outline, replaced by MUI focus rings
      '*:focus-visible': {
        outline: 'none',
      },

      // Custom scrollbar (webkit browsers)
      '*::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '*::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(232, 232, 240, 0.15)',
        borderRadius: '4px',
        '&:hover': {
          backgroundColor: 'rgba(232, 232, 240, 0.25)',
        },
      },

      // Image defaults
      img: {
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
      },

      // Link defaults
      a: {
        textDecoration: 'none',
        color: 'inherit',
      },

      // Button defaults
      'button, input, select, textarea': {
        fontFamily: 'inherit',
      },
    }}
  />
);

/**
 * Theme Provider Props
 */
interface ThemeProviderProps {
  children: ReactNode;
}

/**
 * Theme Provider Component
 * Wraps the application with the dark-mode MUI theme
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {globalStyles}
      {children}
    </MuiThemeProvider>
  );
};
